import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminDashboard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Edit, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import BlogImageEditor from '../../components/BlogImageEditor';
import BlogImage from '../../components/BlogImage';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/blog/posts`, {
        headers: getAuthHeader()
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to fetch blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (postData) => {
    try {
      if (postData.id) {
        await axios.put(
          `${API_URL}/admin/blog/posts/${postData.id}`,
          postData,
          { headers: getAuthHeader() }
        );
        toast.success('Blog post updated successfully');
      } else {
        await axios.post(
          `${API_URL}/admin/blog/posts`,
          postData,
          { headers: getAuthHeader() }
        );
        toast.success('Blog post created successfully');
      }
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to save blog post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/blog/posts/${postId}`, {
        headers: getAuthHeader()
      });
      toast.success('Blog post deleted successfully');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(
        `${API_URL}/admin/blog/posts/${post.id}`,
        { published: !post.published },
        { headers: getAuthHeader() }
      );
      toast.success(post.published ? 'Post unpublished' : 'Post published');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  if (loading) return <AdminLayout title="Blog Posts"><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Manage Blog Posts">
      <div className="space-y-6">
        <Button
          onClick={() => setEditingPost({ 
            title: '', 
            excerpt: '', 
            content: '', 
            category: '',
            image: '',
            author: 'Lindsey Mayr',
            published: false
          })}
          className="bg-sage-700 hover:bg-sage-800 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Post
        </Button>

        <div className="grid gap-6">
          {posts.map((post) => (
            <Card key={post.id} className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex gap-6">
                  {post.image && (
                    <div className="w-32 h-20 flex-shrink-0 overflow-hidden rounded-lg"><BlogImage src={post.image} position={post.imagePosition || post.image_position} alt={post.title} /></div>
                  )}
                  <div className="flex-1 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{post.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{post.category}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          post.published 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-700 line-clamp-2">{post.excerpt}</p>
                    <div className="flex space-x-2 pt-2">
                      <Button
                        onClick={() => togglePublish(post)}
                        variant="outline"
                        size="sm"
                        className="border-sage-300 text-sage-700"
                      >
                        {post.published ? (
                          <>
                            <EyeOff className="mr-2 h-4 w-4" />
                            Unpublish
                          </>
                        ) : (
                          <>
                            <Eye className="mr-2 h-4 w-4" />
                            Publish
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => setEditingPost(post)}
                        variant="outline"
                        size="sm"
                        className="border-sage-300 text-sage-700"
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(post.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {editingPost && (
        <BlogEditModal
          post={editingPost}
          onSave={handleSave}
          onClose={() => setEditingPost(null)}
        />
      )}
    </AdminLayout>
  );
};

const BlogEditModal = ({ post, onSave, onClose }) => {
  const [formData, setFormData] = useState({ ...post, imagePosition: post.imagePosition || post.image_position });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { getAuthHeader } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading || saving) return;
    setSaving(true);
    try { await onSave(formData); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-3xl my-8">
        <CardContent className="p-8">
          <h3 className="text-2xl font-serif mb-6">{post.id ? 'Edit Post' : 'New Post'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category *</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                placeholder="e.g., Health & Wellness, Personal Story"
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image</label>
              <input
                type="file"
                accept="image/*,.heic,.heif,.hif"
                disabled={uploading || saving}
                onChange={async (e) => {
                  const input = e.currentTarget;
                  const file = input.files[0];
                  if (!file) return;
                  if (file.size > 40 * 1024 * 1024) { toast.error('Please choose a photo smaller than 40 MB.'); input.value = ''; return; }
                  setUploading(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    const res = await axios.post(`${API_URL}/admin/blog/upload-image`, fd, { headers: getAuthHeader() });
                    setFormData(prev => ({ ...prev, image: res.data.url, imagePosition: {x:50,y:50,zoom:1} }));
                    toast.success('Photo ready. Adjust the crop below, then save the post.');
                  } catch (error) { toast.error(error.response?.data?.detail || 'Photo upload failed. Please try again.'); }
                  finally { setUploading(false); input.value = ''; }
                }}
                data-testid="blog-image-upload"
                className="w-full px-4 py-3 rounded-lg border-2 border-sage-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200 file:cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1" role="status">{uploading ? 'Uploading and preparing your photo…' : 'PNG, JPG, WebP, AVIF and iPhone HEIC/HEIF photos · up to 40 MB. Photos are automatically oriented and resized.'}</p>
              <p className="text-xs text-gray-500 mt-2">Or enter URL:</p>
              <input
                type="text"
                disabled={uploading || saving}
                value={formData.image || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value, imagePosition: {x:50,y:50,zoom:1} }))}
                placeholder="https://example.com/image.jpg"
                className="w-full mt-1 px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
              />
              {formData.image && (
                <div className="mt-4">
                  <BlogImageEditor
                    src={formData.image}
                    position={formData.imagePosition}
                    onPositionChange={(pos) => setFormData(prev => ({ ...prev, imagePosition: pos }))}
                    aspectRatio="16/9"
                    label="Blog Image Position"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Excerpt *</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                required
                rows={3}
                placeholder="Brief summary of the post"
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Content *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                rows={12}
                placeholder="Full blog post content"
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="h-4 w-4 text-sage-700 border-sage-300 rounded"
              />
              <label htmlFor="published" className="text-sm font-medium">
                Publish immediately
              </label>
            </div>
            <div className="flex space-x-4 pt-4">
              <Button type="submit" disabled={uploading || saving} className="flex-1 bg-sage-700 hover:bg-sage-800 text-white">
                {uploading ? 'Preparing Photo…' : saving ? 'Saving…' : 'Save Post'}
              </Button>
              <Button type="button" disabled={uploading || saving} onClick={onClose} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBlog;
